// HeartButton — iter105.
//
// Dark-circle save button with a slow 3-second breathing glow that draws
// attention to the heart until the user taps it for the first time. The
// floating "Save for later" hint fades in/out on the SAME 3s cycle so the
// two animations are visually locked together.
//
// Behaviour:
//   • First render → if `localStorage.habano.heart_hint_shown` is unset
//     AND the heart is unsaved, the circle glows + the hint text fades on
//     a 3s loop. Both stop the moment the user taps (or once the flag is
//     set by any other heart on the app).
//   • On tap → set `habano.heart_hint_shown = "true"`, kill the loops,
//     run the 400ms tap animation, call `onToggle()`.
//   • Saved state styling (gold tint background, filled icon) is handled
//     entirely by the CSS attribute selector `[data-saved="true"]`.
import { useEffect, useRef, useState } from "react";
import { Heart } from "lucide-react";
import { useI18n } from "../lib/i18n";

const HINT_KEY = "habano.heart_hint_shown";

function getHintShown() {
  try { return localStorage.getItem(HINT_KEY) === "true"; } catch { return true; }
}
function markHintShown() {
  try { localStorage.setItem(HINT_KEY, "true"); } catch { /* ignore */ }
}

export default function HeartButton({
  saved,
  onToggle,
  testid,
  ariaLabel,
  showHint = false,
  className = "",
}) {
  const { t } = useI18n();
  const [tapAnim, setTapAnim] = useState(false);
  const [hinting, setHinting] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    // Start the breathing glow + hint fade on the first mount when the
    // user hasn't tapped any heart yet AND this heart is unsaved.
    if (!saved && !getHintShown()) setHinting(true);
  }, [saved]);

  // If any other heart on the app marks the flag, kill our own hinting.
  useEffect(() => {
    const handle = () => { if (getHintShown()) setHinting(false); };
    window.addEventListener("storage", handle);
    return () => window.removeEventListener("storage", handle);
  }, []);

  const handleClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    // Stop the breathing animations the instant the user interacts.
    if (hinting) { setHinting(false); markHintShown(); }
    if (!saved) {
      setTapAnim(true);
      setTimeout(() => setTapAnim(false), 420);
    }
    onToggle?.();
  };

  return (
    <span style={{ position: "relative", display: "inline-block" }} className={className}>
      <button
        type="button"
        onClick={handleClick}
        data-testid={testid}
        aria-label={ariaLabel || (saved ? "Unsave" : "Save")}
        className="habano-heart"
        data-saved={saved ? "true" : "false"}
        data-tap-animating={tapAnim ? "true" : "false"}
        data-glow={hinting ? "true" : "false"}
        style={{ border: "none" }}
      >
        <Heart strokeWidth={1.5} />
      </button>
      {showHint && hinting && (
        <span
          className="habano-heart-hint"
          data-testid={testid ? `${testid}-hint` : undefined}
          style={{ top: 36, right: 0 }}
        >
          {t("heart_hint_save")}
        </span>
      )}
    </span>
  );
}
